import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  FiTag,
  FiUsers,
  FiHeart,
  FiCheckCircle,
  FiClock,
} from "react-icons/fi";
import { toast } from "sonner";

const DiscoverPage = ({
  productDealsData,
  productDealsError,
  activitiesData,
  activitiesError,
  vendorsData,
  vendorsError,
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [productDealCount, setProductDealCount] = useState("...");
  const [activityCount, setActivityCount] = useState("...");
  const [vendorCount, setVendorCount] = useState("...");

  const navigate = useNavigate();

  // Helper to safely parse API response for count
  const parseCount = (data) => {
    if (Array.isArray(data?.data)) {
      return data.data.length.toLocaleString();
    } else if (data && typeof data.count === "number") {
      return data.count.toLocaleString();
    } else if (data && Array.isArray(data.vendors)) {
      return data.vendors.length.toLocaleString();
    } else if (Array.isArray(data)) {
      return data.length.toLocaleString();
    } else if (data && Array.isArray(data.results)) {
      return data.count
        ? data.count.toLocaleString()
        : data.results.length.toLocaleString();
    } else if (data && Array.isArray(data.deals)) {
      return data.deals.length.toLocaleString();
    }
    return "N/A";
  };

  useEffect(() => {
    if (productDealsData) {
      setProductDealCount(parseCount(productDealsData));
    }
    if (productDealsError) {
      toast.error(
        "Failed to load product deals count. Please check your connection."
      );
      setProductDealCount("Error");
    }
  }, [productDealsData, productDealsError]);

  useEffect(() => {
    if (activitiesData) {
      setActivityCount(parseCount(activitiesData));
    }
    if (activitiesError) {
      console.error("Error fetching activities count:", activitiesError);
      setActivityCount("Error");
      toast.error("Failed to load activities count.");
    }
  }, [activitiesData, activitiesError]);

  useEffect(() => {
    if (vendorsData) {
      setVendorCount(parseCount(vendorsData));
    }
    if (vendorsError) {
      console.error("Error fetching vendor count:", vendorsError);
      setVendorCount("N/A");
      toast.error("Failed to load vendor count.");
    }
  }, [vendorsData, vendorsError]);

  useEffect(() => {
    // Set loading to false when all data is loaded or errors occur
    if (
      (productDealsData || productDealsError) &&
      (activitiesData || activitiesError) &&
      (vendorsData || vendorsError)
    ) {
      setIsLoading(false);
    }
  }, [
    productDealsData,
    productDealsError,
    activitiesData,
    activitiesError,
    vendorsData,
    vendorsError,
  ]);

  if (isLoading) {
    return (
      <div className="flex flex-col justify-center items-center min-h-screen bg-gray-100 shadow-xl">
        <div className="animate-spin rounded-full h-20 w-20 border-t-4 border-b-4 border-orange-500"></div>
        <p className="mt-4 text-lg font-semibold text-gray-700">
          Processing...
        </p>
      </div>
    );
  }

  return (
    <div className="min-h-[700px] bg-gradient-to-r from-orange-50 to-orange-100 flex items-center shadow-md justify-start pt-1 sm:p-6 lg:p-8">
      <div className="text-center w-full max-w-4xl mx-auto mb-4">
        <h1 className="text-4xl md:text-5xl font-bold text-black mb-4 sm:mb-4">
          Connect with Your Community, Book Services & Find Deals
        </h1>
        <p className="sm:text-lg text-gray-600 mb-10 px-4 max-w-2xl mx-auto">
          Your one-stop platform for exclusive product deals, professional
          services, and exciting local activities. Join thousands of satisfied
          users in your area.
        </p>

        {/* App Download Buttons */}
        <div className="flex flex-col items-center space-y-4 mb-12 md:flex-row md:justify-center md:space-y-0 md:space-x-4">
          <a
            href="https://apps.apple.com/us/app/upswap/id6478142973"
            target="_blank"
            rel="noopener noreferrer"
          >
            <img
              src="/Screenshot 2025-06-10 183311.png"
              alt="Download on the App Store"
              className="h-12"
            />
          </a>
          <a
            href="https://play.google.com/store/apps/details?id=com.spectralshifttech.upswap"
            target="_blank"
            rel="noopener noreferrer"
          >
            <img
              src="/Screenshot 2025-06-10 183323.png"
              alt="GET IT ON Google Play"
              className="h-12"
            />
          </a>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 mb-10 w-full max-w-7xl mx-auto px-4">
          {/* Card 1: Service Deals */}
          <div className="bg-white p-3 lg:p-2 rounded-lg shadow-md flex flex-col items-center justify-center text-center pt-6 pb-2 text-sm lg:text-xs">
            <FiTag className="text-orange-500 w-6 h-6 mb-2" />
            <p className="font-semibold text-gray-800">Service Deals</p>
            <p className="text-gray-500 mt-0.5">{productDealCount} listings</p>
          </div>

          {/* Card 2: Activities */}
          <div className="bg-white p-3 lg:p-2 rounded-lg shadow-md flex flex-col items-center justify-center text-center pt-6 pb-2 text-sm lg:text-xs">
            <FiUsers className="text-orange-500 w-6 h-6 mb-2" />
            <p className="font-semibold text-gray-800">Activities</p>
            <p className="text-gray-500 mt-0.5">{activityCount} listings</p>
          </div>

          {/* Card 3: Vendors */}
          <div className="bg-white p-3 lg:p-2 rounded-lg shadow-md flex flex-col items-center justify-center text-center pt-6 pb-2 text-sm lg:text-xs">
            <FiHeart className="text-orange-500 w-6 h-6 mb-2" />
            <p className="font-semibold text-gray-800">Vendors</p>
            <p className="text-gray-500 mt-0.5">{vendorCount} listings</p>
          </div>

          {/* Card 4: Trending */}
          <div className="bg-white p-3 lg:p-2 rounded-lg shadow-md flex flex-col items-center justify-center text-center pt-6 pb-2 text-sm lg:text-xs">
            <FiHeart className="text-orange-500 w-6 h-6 mb-2" />
            <p className="font-semibold text-gray-800">Trending</p>
            <p className="text-gray-500 mt-0.5">{vendorCount} listings</p>
          </div>
        </div>

        {/* Features Section - Moved out of the categories grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12 w-full max-w-6xl mx-auto">
          {/* Feature 1: Verified Vendors */}
          <div className="flex flex-col items-center text-center p-4">
            <div className="bg-orange-100 p-4 rounded-full mb-4 flex items-center justify-center">
              <FiCheckCircle className="text-orange-500 w-6 h-6" />
            </div>
            <h3 className="font-semibold text-gray-800 text-lg mb-2">
              Verified Vendors
            </h3>
            <p className="text-gray-600">
              All vendors are thoroughly vetted and verified
            </p>
          </div>

          {/* Feature 2: Trusted Community */}
          <div className="flex flex-col items-center text-center">
            <div className="bg-orange-100 p-4 rounded-full mb-4 flex items-center justify-center">
              <FiUsers className="text-orange-500 w-6 h-6" />
            </div>
            <h3 className="font-semibold text-gray-800 text-lg mb-2">
              Trusted Community
            </h3>
            <p className="text-gray-600">
              Join a community of like-minded individuals
            </p>
          </div>

          {/* Feature 3: Instant Booking */}
          <div className="flex flex-col items-center text-center p-4">
            <div className="bg-orange-100 p-4 rounded-full mb-4 flex items-center justify-center">
              <FiClock className="text-orange-500 w-6 h-6" />
            </div>
            <h3 className="font-semibold text-gray-800 text-lg mb-2">
              Instant Booking
            </h3>
            <p className="text-gray-600">
              Book services and activities in real-time
            </p>
          </div>
        </div>

        <div className="flex flex-col md:flex-row items-center justify-center gap-4 w-full max-w-2xl mx-auto px-4">
          <button
            className="bg-orange-500 text-white text-sm md:text-base px-6 py-2 md:py-3 rounded-md hover:bg-orange-600 transition duration-300 shadow-md w-[200px]"
            onClick={() => navigate("/LocalActivities")}
          >
            Explore Activities
          </button>
          <button
            className="bg-white text-gray-700 border border-gray-300 text-sm md:text-base px-6 py-2 md:py-3 rounded-md hover:bg-gray-100 transition duration-300 shadow-md w-[200px]"
            onClick={() => navigate("/PopularDeals")}
          >
            Browse Deals
          </button>
        </div>
      </div>
    </div>
  );
};

export default DiscoverPage;
